import ResizeObserver from 'resize-observer-polyfill';

import { Utilities } from '../Utilities';

const applyLegacyCss = (
  font: string,
  primary: string,
  secondary: string,
  tertiary: string,
): void => {
  const styleRules = [];

  styleRules.push(
    `.fd-typeahead>.form-control{background:${primary}!important;color:${tertiary}!important;}`,
  );
  styleRules.push(
    `.fd-typeahead>.form-control.focus{border-color:${secondary}!important;}`,
  );
  styleRules.push(
    `.dark .fd-typeahead>.form-control{background:${tertiary}!important;color:${primary}!important;border-color:${primary}!important;}`,
  );
  styleRules.push(
    `.dark .fd-typeahead>.form-control.focus{border-color:${primary}!important;}`,
  );
  styleRules.push(`.dropdown-menu{background-color:${primary}!important;}`);
  styleRules.push(`.dropdown-menu>li>a{color:${tertiary}!important;}`);
  styleRules.push(
    `.dark .dropdown-menu{background-color:${tertiary}!important;}`,
  );
  styleRules.push(`.dark .dropdown-menu>li>a{color:${primary}!important;}`);

  styleRules.push(`#flipdish-menu .dropdown-menu, #flipdish-menu .form-control, #flipdish-menu .h1, #flipdish-menu .h2, #flipdish-menu .h3, #flipdish-menu .h4, #flipdish-menu .panel-heading, #flipdish-menu .well, #flipdish-menu input, #flipdish-menu textarea, #flipdish-menu a, #flipdish-menu h1, #flipdish-menu h2, #flipdish-menu h3, #flipdish-menu h4, #flipdish-menu li, #flipdish-menu p, #flipdish-menu span {
    font-family: "${font}", "sans-serif";
  }`);

  const style = document.createElement('style');
  style.type = 'text/css';
  style.innerHTML = styleRules.join('\n');
  document.getElementsByTagName('head')[0].appendChild(style);
};

const applyLegacyFontCss = (fontName: string): void => {
  const style = document.createElement('style');
  style.type = 'text/css';
  style.innerHTML = `#flipdish-menu .dropdown-menu, #flipdish-menu .form-control, #flipdish-menu .h1, #flipdish-menu .h2, #flipdish-menu .h3, #flipdish-menu .h4, #flipdish-menu .panel-heading, #flipdish-menu .well, #flipdish-menu input, #flipdish-menu textarea, #flipdish-menu a, #flipdish-menu h1, #flipdish-menu h2, #flipdish-menu h3, #flipdish-menu h4, #flipdish-menu li, #flipdish-menu p, #flipdish-menu span {
    font-family: "${fontName}", "sans-serif";
  }`;
  document.getElementsByTagName('head')[0].appendChild(style);
};

const createResizeObserver = (): ResizeObserver => {
  // Create a single ResizeObserver instance to handle all
  // container elements. The instance is created with a callback,
  // which is invoked as soon as an element is observed as well
  // as any time that element's size changes.
  const resizeObserver = new ResizeObserver((entries) => {
    const defaultBreakpoints = { XS: 384, SM: 768, MD: 992, LG: 1200 };

    entries.forEach((entry) => {
      // If breakpoints are defined on the observed element,
      // use them. Otherwise use the defaults.
      const breakpoints = (entry.target as any).dataset.breakpoints
        ? JSON.parse((entry.target as any).dataset.breakpoints)
        : defaultBreakpoints;

      // Update the matching breakpoints on the observed element.
      Object.keys(breakpoints).forEach((breakpoint) => {
        const minWidth = breakpoints[breakpoint];
        if (entry.contentRect.width >= minWidth) {
          entry.target.classList.add(breakpoint);
        } else {
          entry.target.classList.remove(breakpoint);
        }
      });
    });
  });

  return resizeObserver;
};

const generateFontLink = (font: string, weights: string): HTMLLinkElement => {
  const fontName = font.replace(' ', '+');

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.type = 'text/css';
  link.href = `https://fonts.googleapis.com/css?family=${fontName}:${weights}&display=optional`;
  return link;
};

const generateFont2Link = (font: string, weights: string): HTMLLinkElement => {
  const fontName = font.replace(' ', '+');

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  // link.setAttribute('type', 'text/css');
  link.type = 'text/css';
  link.href = `https://fonts.googleapis.com/css2?family=${fontName}:wght@${weights}&display=block`;
  return link;
};

const loadFont = (font: string = null, loadCss2 = true): void => {
  const link = document.createElement('link');
  link.rel = 'preconnect';
  link.href = 'https://fonts.gstatic.com';

  const redesignFont = font || 'Poppins';
  const legacyFont = font || 'Roboto';
  const whitelabelFont = loadCss2
    ? { name: redesignFont, weights: '400;500;600' }
    : { name: legacyFont, weights: '400,400i,700' };

  const fontLink = loadCss2
    ? generateFont2Link(whitelabelFont.name, whitelabelFont.weights)
    : generateFontLink(whitelabelFont.name, whitelabelFont.weights);

  document.getElementsByTagName('head')[0].appendChild(fontLink);
};

const skipToContent = (): void => {
  const firstEl = document.querySelector('#skip-to-content');
  if (firstEl) {
    Utilities.ensureVisible(firstEl);
  }
};

export const domUtils = {
  applyLegacyCss,
  applyLegacyFontCss,
  createResizeObserver,
  generateFontLink,
  loadFont,
  skipToContent,
};
